import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Button,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch
} from '@mui/material';
import { Plus, Trash2, SquarePen } from 'lucide-react';
import { getAtmospheres, createAtmosphere, updateAtmosphere, deleteAtmosphere } from '../../../Services/AddRetreatManagement/RetreatDetailsApi';
import DeleteModal from '../../../Components/Modals/DeleteModal';
import PermissionWrapper from '../../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../../Components/Common/GlobalSanckbar';


const GetRetreatAtmosphere = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const [atmospheres, setAtmospheres] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal states
    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        displayOrder: '1',
        isActive: true
    });
    const [currentItem, setCurrentItem] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [modalError, setModalError] = useState(null);

    // Delete modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const fetchAtmospheres = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getAtmospheres();
            if (response && response.success) {
                setAtmospheres(response.data);
            } else {
                setError("Failed to load atmospheres.");
            }
        } catch (err) {
            setError(err.response?.data?.message || "Failed to fetch atmospheres. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAtmospheres();
    }, [fetchAtmospheres]);

    const handleOpenModal = (item = null) => {
        setModalError(null);
        setFieldErrors({});
        if (item) {
            setIsEdit(true);
            setCurrentItem(item);
            setFormData({
                title: item.title,
                displayOrder: item.displayOrder !== undefined && item.displayOrder !== null && item.displayOrder !== '' ? String(item.displayOrder) : '1',
                isActive: item.isActive !== undefined ? item.isActive : true
            });
        } else {
            setIsEdit(false);
            setCurrentItem(null);
            setFormData({
                title: '',
                displayOrder: '1',
                isActive: true
            });
        }
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setIsEdit(false);
        setCurrentItem(null);
        setFormData({ title: '', displayOrder: '1', isActive: true });
        setModalError(null);
        setFieldErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value, checked, type } = e.target;
        if (name === 'displayOrder') {
            if (value !== "" && (!/^\d+$/.test(value) || Number(value) > 999)) {
                return;
            }
        }
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (fieldErrors[name]) {
            setFieldErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const handleSubmit = async () => {
        if (!formData.title) {
            setFieldErrors({ title: "Title is required" });
            setModalError("Please fill in all required fields");
            return;
        }

        if (formData.displayOrder !== "" && formData.displayOrder !== null && formData.displayOrder !== undefined && (Number(formData.displayOrder) > 999 || Number(formData.displayOrder) < 1)) {
            setFieldErrors({ displayOrder: "Display order must be between 1 and 999." });
            setModalError("Please check field validation errors");
            return;
        }

        setSubmitting(true);
        setModalError(null);
        setFieldErrors({});

        const payload = {
            title: formData.title,
            isActive: formData.isActive,
            ...(formData.displayOrder !== "" && formData.displayOrder !== null && formData.displayOrder !== undefined ? { displayOrder: Number(formData.displayOrder) } : {})
        };

        try {
            if (isEdit) {
                await updateAtmosphere(currentItem.id, payload);
            } else {
                await createAtmosphere(payload);
            }

            handleCloseModal();
            fetchAtmospheres();
            showSnackbar(`Atmosphere ${isEdit ? 'updated' : 'created'} successfully!`, 'success');
        } catch (err) {
            console.error("Save Atmosphere Error:", err);
            if (err.response && err.response.data) {
                const resData = err.response.data;
                if (resData.details || resData.errors) {
                    const errors = {};
                    if (resData.details) {
                        resData.details.forEach(detail => {
                            const fieldName = detail.field === 'general' ? 'title' : detail.field;
                            errors[fieldName] = detail.message;
                        });
                    }
                    setFieldErrors(errors);

                    let errorMsg = resData.errors?.length > 0
                        ? resData.errors.join(", ")
                        : resData.details?.length > 0
                            ? resData.details[0].message
                            : resData.message || "Validation failed";
                    setModalError(errorMsg);
                } else {
                    setModalError(resData.message || `Failed to ${isEdit ? 'update' : 'create'} atmosphere`);
                }
            } else {
                setModalError(err.message || `Failed to ${isEdit ? 'update' : 'create'} atmosphere`);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedId(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (selectedId) {
            try {
                setIsDeleting(true);
                setDeleteError(null);
                const response = await deleteAtmosphere(selectedId);
                if (response && response.success) {
                    setDeleteModalOpen(false);
                    setSelectedId(null);
                    fetchAtmospheres();
                } else {
                    const serverError = response;
                    const errorMsg = Array.isArray(serverError?.errors)
                        ? serverError.errors.join(", ")
                        : (serverError?.details?.[0]?.message || serverError?.message || "Failed to delete atmosphere");
                    setDeleteError(errorMsg);
                }
            } catch (err) {
                console.error("Delete Error:", err);
                const serverError = err.response?.data;
                const errorMessage = Array.isArray(serverError?.errors)
                    ? serverError.errors.join(", ")
                    : (serverError?.details?.[0]?.message || serverError?.message || err.message || "Failed to delete atmosphere");
                setDeleteError(errorMessage);
            } finally {
                setIsDeleting(false);
            }
        }
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.75rem', md: '1.8rem' }, fontWeight: 500 }}>
                        Retreat Atmospheres
                    </Typography>
                    <PermissionWrapper permission="create" mode="disable">
                        <Button
                            disableElevation disableFocusRipple disableRipple disableTouchRipple
                            variant="contained"
                            startIcon={<Plus size={18} />}
                            onClick={() => handleOpenModal()}
                            sx={{
                                bgcolor: '#FFBD65',
                                px: 2.5,
                                color: '#fff',
                                borderRadius: '6px',
                                height: '40px',
                                textTransform: 'none',
                                fontSize: '0.85rem'
                            }}
                        >
                            Add Atmosphere
                        </Button>
                    </PermissionWrapper>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

                <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ minWidth: 950 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '60px' }}>S.No</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Title</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '110px' }}>Display Order</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Created At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Updated At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px', textAlign: 'right' }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                                            <CircularProgress sx={{ color: '#FFBD65' }} size={24} />
                                        </TableCell>
                                    </TableRow>
                                ) : atmospheres.length > 0 ? (
                                    atmospheres.map((item, idx) => (
                                        <TableRow key={item.id} sx={{
                                            transition: 'background-color 0.2s',
                                            '& td': { borderBottom: '1px solid #F0F0F0' },
                                            '&:last-child td': { borderBottom: 0 }
                                        }}>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>{idx + 1}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', fontWeight: 500, color: '#333' }}>{item.title}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666' }}>{item.displayOrder !== undefined && item.displayOrder !== null ? item.displayOrder : 1}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                <Box sx={{
                                                    display: 'inline-block',
                                                    px: 1.5, py: 0.3,
                                                    borderRadius: '4px',
                                                    bgcolor: item.isActive ? '#E8F5E9' : '#FEECEB',
                                                    color: item.isActive ? '#2E7D32' : '#F44336',
                                                    fontSize: '0.7rem',
                                                    fontWeight: 600
                                                }}>
                                                    {item.isActive ? "Active" : "Inactive"}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {item.cuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {item.uuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(item.createdAt)}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(item.updatedAt)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5 }}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <PermissionWrapper permission="edit" mode="disable">
                                                        <Tooltip title="Edit">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple size="small" sx={{ color: '#FFBD65' }} onClick={() => handleOpenModal(item)}>
                                                                <SquarePen size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                    <PermissionWrapper permission="delete" mode="disable">
                                                        <Tooltip title="Delete">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple size="small" sx={{ color: '#FF4D49' }} onClick={() => handleDeleteClick(item.id)}>
                                                                <Trash2 size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#AAA', fontSize: '0.8rem' }}>
                                            No atmospheres found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Dialog open={openModal} onClose={handleCloseModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '6px' } }}>
                    <DialogTitle sx={{ fontWeight: 600, color: '#222', fontSize: '1.1rem', pb: 1 }}>
                        {isEdit ? 'Edit Atmosphere' : 'Add Atmosphere'}
                    </DialogTitle>
                    <DialogContent>
                        {modalError && (
                            <Alert severity="error" sx={{ mb: 2, borderRadius: '6px' }} onClose={() => setModalError(null)}>
                                {modalError}
                            </Alert>
                        )}
                        <Stack spacing={3} sx={{ mt: 2 }}>
                            <TextField
                                fullWidth
                                label="Title"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                placeholder="e.g. Peaceful"
                                error={Boolean(fieldErrors.title)}
                                helperText={fieldErrors.title}
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />
                            <TextField
                                fullWidth
                                type="number"
                                label="Display Order"
                                name="displayOrder"
                                value={formData.displayOrder}
                                onChange={handleInputChange}
                                placeholder="Enter display order (1 - 999)"
                                inputProps={{ min: 1, max: 999, onWheel: (e) => e.target.blur() }}
                                error={Boolean(fieldErrors.displayOrder)}
                                helperText={fieldErrors.displayOrder}
                                size="small"
                                sx={{
                                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                        WebkitAppearance: 'none',
                                        margin: 0,
                                    },
                                    '& input[type=number]': {
                                        MozAppearance: 'textfield',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="isActive"
                                        checked={formData.isActive}
                                        onChange={handleInputChange}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFBD65' },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FFBD65' }
                                        }}
                                    />
                                }
                                label={<span style={{ fontSize: '0.85rem' }}>Active Status</span>}
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, pt: 1 }}>
                        <Button onClick={handleCloseModal} sx={{ color: '#222', textTransform: 'none', fontSize: '0.85rem', px: 2.5 }}>Cancel</Button>
                        <Button
                            disableElevation disableFocusRipple disableRipple disableTouchRipple
                            onClick={handleSubmit}
                            disabled={submitting}
                            variant="contained"
                            sx={{
                                bgcolor: '#FFBD65',
                                color: '#fff',
                                textTransform: 'none',
                                borderRadius: '6px',
                                px: 3,
                                fontSize: '0.85rem'
                            }}
                        >
                            {submitting ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Update' : 'Add')}
                        </Button>
                    </DialogActions>
                </Dialog>

                <DeleteModal
                    open={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    onDelete={handleConfirmDelete}
                    title="Delete Atmosphere"
                    description="Are you sure you want to delete this atmosphere? This action cannot be undone."
                    error={deleteError}
                    loading={isDeleting}
                />
            </Box>
        </PermissionWrapper>
    );
};

export default GetRetreatAtmosphere;